/**
 * Colonnes ProposalSection — grille Mantine 12 colonnes + responsive.
 */

import {
  collectLeafColumnIds,
  defaultNestedLayout,
  flatRowToNested,
  migrateVerticalToNested,
  normalizeNested,
} from './proposalSectionNested';

export const GRID_COLUMNS = 12;

export const RESPONSIVE_BREAKPOINTS = ['base', 'xs', 'sm', 'md', 'lg', 'xl'];

export const DEFAULT_SECTION_COLUMNS = {
  preset: 'row',
  columnIds: ['col-1'],
  columnSpans: [],
};

/** @typedef {number | Record<string, number>} SectionColumnSpan */

export function createSectionColumnId() {
  return `col-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`;
}

function createColumnId() {
  return createSectionColumnId();
}

function clampSpan(n) {
  const v = Math.round(Number(n)) || 1;
  return Math.max(1, Math.min(GRID_COLUMNS, v));
}

/** Span éditable (colonnes non finales) : 1–11. */
function clampPartialSpan(n) {
  return Math.max(1, Math.min(GRID_COLUMNS - 1, Math.round(Number(n)) || 1));
}

function isSpanObject(span) {
  return span != null && typeof span === 'object' && !Array.isArray(span);
}

/** Valeur de span à un breakpoint (nombre simple ou objet responsive). */
export function getSpanAt(span, breakpoint = 'md') {
  if (typeof span === 'number') return clampSpan(span);
  if (!isSpanObject(span)) return GRID_COLUMNS;

  const order = RESPONSIVE_BREAKPOINTS;
  const start = order.indexOf(breakpoint);
  for (let i = start; i >= 0; i -= 1) {
    const key = order[i];
    if (span[key] != null) return clampSpan(span[key]);
  }
  return GRID_COLUMNS;
}

/** Normalise en objet responsive avec empilement mobile par défaut. */
export function normalizeSpanInput(span, { stackOnMobile = true } = {}) {
  if (typeof span === 'number') {
    const md = clampSpan(span);
    return stackOnMobile ? { base: GRID_COLUMNS, md } : md;
  }
  if (!isSpanObject(span)) {
    return stackOnMobile ? { base: GRID_COLUMNS, md: 6 } : 6;
  }
  if (stackOnMobile && span.base == null) {
    return { base: GRID_COLUMNS, ...span };
  }
  return { ...span };
}

function simplifySpanObject(spanByBp) {
  const values = RESPONSIVE_BREAKPOINTS.map((bp) => spanByBp[bp]).filter((v) => v != null);
  if (values.length === 0) return GRID_COLUMNS;
  if (values.every((v) => v === values[0])) return values[0];
  return spanByBp;
}

/** Spans complets (N valeurs) pour Grid.Col, dernière colonne calculée. */
export function getResolvedGridSpans(columnIds, columnSpans = [], options = {}) {
  const n = columnIds?.length ?? 0;
  if (n === 0) return [];
  if (n === 1) return [GRID_COLUMNS];

  const partial = columnSpans.slice(0, n - 1).map((s) => normalizeSpanInput(s, options));
  const result = [...partial];

  const lastByBp = {};
  RESPONSIVE_BREAKPOINTS.forEach((bp) => {
    const fixedSpans = partial.map((s) => getSpanAt(s, bp));
    const sumFixed = fixedSpans.reduce((sum, s) => sum + s, 0);
    const stacked = fixedSpans.some((s) => s >= GRID_COLUMNS) || sumFixed >= GRID_COLUMNS;
    lastByBp[bp] = stacked
      ? GRID_COLUMNS
      : Math.max(1, GRID_COLUMNS - sumFixed);
  });
  result.push(simplifySpanObject(lastByBp));

  return result;
}

/**
 * Point d'empilement (prop plate `stackAtPx` de ProposalSection) : les
 * colonnes s'empilent quand le viewport est ≤ à cette largeur. Valeurs
 * alignées sur les viewports Puck (Small 360, Medium 768) ; 0 = jamais.
 */
export const STACK_AT_SMALL_PX = 360;
export const STACK_AT_MEDIUM_PX = 768;
export const STACK_NEVER_PX = 0;
export const DEFAULT_STACK_AT_PX = STACK_AT_MEDIUM_PX;

/** Normalise la prop (absente/invalide → défaut medium). */
export function resolveStackAtPx(value) {
  const n = Number(value);
  if (!Number.isFinite(n) || n < 0) return DEFAULT_STACK_AT_PX;
  return Math.round(n);
}

/**
 * Spans pour Grid.Col selon le point d'empilement : 0 (jamais) → span
 * desktop constant à tous les breakpoints ; sinon { base: 12, sm: span } —
 * la Grid porte alors un breakpoint `sm` custom (voir stackBreakpointsProp).
 */
export function applyStackBreakpointToSpans(spans, stackAtPx) {
  if (resolveStackAtPx(stackAtPx) === STACK_NEVER_PX) {
    return spans.map((s) => getSpanAt(s, 'md'));
  }
  return spans.map((s) => ({ base: GRID_COLUMNS, sm: getSpanAt(s, 'md') }));
}

/**
 * Prop `breakpoints` de la Grid Mantine : grille dès que le viewport
 * DÉPASSE stackAtPx (min-width = stackAtPx + 1). undefined si jamais empilé.
 */
export function stackBreakpointsProp(stackAtPx) {
  const px = resolveStackAtPx(stackAtPx);
  if (px === STACK_NEVER_PX) return undefined;
  return { sm: `${px + 1}px` };
}

/** Spans md (desktop) pour affichage éditeur. */
export function getColumnSpanValues(columnIds, columnSpans = []) {
  return getResolvedGridSpans(columnIds, columnSpans).map((s) => getSpanAt(s, 'md'));
}

/** Répartit 12 colonnes également → columnSpans (sans la dernière). */
export function equalColumnSpans(count, { stackOnMobile = true } = {}) {
  if (count <= 1) return [];
  const md = Math.floor(GRID_COLUMNS / count);
  const span = stackOnMobile ? { base: GRID_COLUMNS, md } : md;
  return Array(count - 1).fill(span);
}

/** @deprecated Ancien % → spans partiels. */
export function getColumnWidthPercents(columnIds, columnWidths = []) {
  const spans = getColumnSpanValues(columnIds, widthsToPartialSpans(columnIds, columnWidths));
  return spans.map((s) => Math.round((s / GRID_COLUMNS) * 10000) / 100);
}

function widthsToPartialSpans(columnIds, columnWidths) {
  const n = columnIds.length;
  if (n <= 1) return [];

  const fixed = columnWidths.slice(0, n - 1).map((w) => Math.max(1, Math.round((Number(w) || 0) / 100 * GRID_COLUMNS)));
  const sumFixed = fixed.reduce((a, b) => a + b, 0);
  const last = Math.max(1, GRID_COLUMNS - sumFixed);
  const all = [...fixed, last];
  return all.slice(0, -1).map((md) => ({ base: GRID_COLUMNS, md }));
}

/** Ancien tableau columnSpans (toutes colonnes) → columnSpans partiels. */
export function legacyFullSpansToPartial(fullSpans) {
  if (!Array.isArray(fullSpans) || fullSpans.length <= 1) return [];
  return fullSpans.slice(0, -1).map((s) => (
    typeof s === 'number'
      ? { base: GRID_COLUMNS, md: clampSpan(s) }
      : normalizeSpanInput(s)
  ));
}

/** Ancien spansToColumnWidths — conservé pour tests de migration % . */
export function spansToColumnWidths(columnSpans) {
  if (!Array.isArray(columnSpans) || columnSpans.length <= 1) return [];
  const total = columnSpans.reduce((a, b) => a + (Number(b) || 0), 0) || GRID_COLUMNS;
  const pcts = columnSpans.map((s) => Math.round(((Number(s) || 0) / total) * 10000) / 100);
  return pcts.slice(0, -1);
}


/** Infère le preset depuis la structure (données legacy sans champ preset). */
export function inferSectionPreset(columnIds, columnSpans, rawSectionColumns) {
  if (rawSectionColumns?.preset === 'nested' || rawSectionColumns?.nested) {
    return 'nested';
  }
  if (rawSectionColumns?.preset === 'vertical') {
    return 'nested';
  }
  return 'row';
}

function resolveColumnLayout(props = {}) {
  const raw = props.sectionColumns;

  if (raw?.columnIds?.length) {
    const ids = [...raw.columnIds];
    if (Array.isArray(raw.columnSpans)) {
      return {
        columnIds: ids,
        columnSpans: raw.columnSpans.slice(0, Math.max(0, ids.length - 1)),
      };
    }
    if (Array.isArray(raw.columnWidths)) {
      return {
        columnIds: ids,
        columnSpans: widthsToPartialSpans(ids, raw.columnWidths),
      };
    }
    return {
      columnIds: ids,
      columnSpans: equalColumnSpans(ids.length),
    };
  }

  const ids = Array.isArray(props.columnIds) && props.columnIds.length > 0
    ? [...props.columnIds]
    : [...DEFAULT_SECTION_COLUMNS.columnIds];

  if (Array.isArray(props.columnSpans) && props.columnSpans.length > 0) {
    const partial = props.columnSpans.length >= ids.length
      ? legacyFullSpansToPartial(props.columnSpans)
      : props.columnSpans.map((s) => (
        typeof s === 'number' ? { base: GRID_COLUMNS, md: clampSpan(s) } : normalizeSpanInput(s)
      ));
    return { columnIds: ids, columnSpans: partial.slice(0, ids.length - 1) };
  }

  if (Array.isArray(props.columnWidths)) {
    return {
      columnIds: ids,
      columnSpans: widthsToPartialSpans(ids, props.columnWidths),
    };
  }

  return {
    columnIds: ids,
    columnSpans: equalColumnSpans(ids.length),
  };
}

/**
 * Normalise props section (nouveau + legacy).
 */
export function resolveSectionColumns(props = {}) {
  const raw = props.sectionColumns;
  const preset = raw?.preset
    || inferSectionPreset(null, null, raw)
    || 'row';

  if (preset === 'nested' || raw?.nested || raw?.preset === 'vertical') {
    let nested;
    if (raw?.nested) {
      nested = normalizeNested(raw.nested);
      const leafIds = collectLeafColumnIds(nested);
      if (raw.columnIds?.length && !raw.columnIds.every((id) => leafIds.includes(id))) {
        nested = flatRowToNested(raw.columnIds, raw.columnSpans);
      }
    } else if (raw?.preset === 'vertical') {
      nested = migrateVerticalToNested(raw?.verticalColumnCount);
    } else if (raw?.columnIds?.length) {
      nested = flatRowToNested(raw.columnIds, raw.columnSpans);
    } else {
      nested = defaultNestedLayout();
    }
    return {
      preset: 'nested',
      nested,
      columnIds: collectLeafColumnIds(nested),
      columnSpans: [],
    };
  }

  const layout = resolveColumnLayout(props);

  return {
    preset: 'row',
    nested: null,
    columnIds: layout.columnIds,
    columnSpans: layout.columnSpans,
  };
}

export function zoneKeyForSectionColumn(sectionId, columnId) {
  return `${sectionId}:col-${columnId}`;
}

export function addSectionColumn(sectionColumns) {
  const { columnIds, columnSpans } = resolveSectionColumns({ sectionColumns });
  const newIds = [...columnIds, createColumnId()];
  return {
    preset: 'row',
    columnIds: newIds,
    columnSpans: equalColumnSpans(newIds.length),
  };
}

export function removeSectionColumn(sectionColumns, indexToRemove) {
  const { columnIds, columnSpans } = resolveSectionColumns({ sectionColumns });
  if (columnIds.length <= 1 || indexToRemove < 0 || indexToRemove >= columnIds.length) {
    return { columnIds, columnSpans, removedColumnId: null };
  }

  const removedColumnId = columnIds[indexToRemove];
  const mdSpans = getColumnSpanValues(columnIds, columnSpans);
  mdSpans.splice(indexToRemove, 1);

  const newIds = columnIds.filter((_, i) => i !== indexToRemove);
  const newPartial = mdSpans.length <= 1
    ? []
    : mdSpans.slice(0, -1).map((md) => ({ base: GRID_COLUMNS, md: clampSpan(md) }));

  return {
    preset: 'row',
    columnIds: newIds,
    columnSpans: newPartial,
    removedColumnId,
  };
}

export function setSectionColumnSpan(sectionColumns, index, spanValue) {
  const { columnIds, columnSpans } = resolveSectionColumns({ sectionColumns });
  const n = columnIds.length;
  if (n <= 1 || index < 0 || index >= n - 1) {
    return { columnIds, columnSpans };
  }

  const next = [...columnSpans];
  const md = clampPartialSpan(spanValue);
  const othersSum = next.reduce((sum, s, i) => (
    i === index ? sum : sum + getSpanAt(s, 'md')
  ), 0);
  const maxForIndex = GRID_COLUMNS - 1 - othersSum;
  const clamped = Math.max(1, Math.min(maxForIndex, md));
  next[index] = { base: GRID_COLUMNS, md: clamped };

  return {
    preset: 'row',
    columnIds,
    columnSpans: next,
  };
}

/** @deprecated */
export function equalColumnWidths(count) {
  return equalColumnSpans(count).map((s) => getSpanAt(s, 'md') / GRID_COLUMNS * 100);
}

/** @deprecated */
export function setSectionColumnWidth(sectionColumns, index, widthPct) {
  const span = Math.round((Number(widthPct) / 100) * GRID_COLUMNS);
  return setSectionColumnSpan(sectionColumns, index, span);
}
